import { useCallback, useMemo, useState } from "react"
import { PopoverComboboxItem } from "shared-web/components/composite/popover"

import { UsePopoverMultiSelectProps, UsePopoverMultiSelectRes } from "./types"

function usePopoverMultiSelect<T>({
  defaultSelected,
  items,
  keyExtractor,
  labelExtractor,
  accessoryExtractor,
  contentExtractor,
  onChangeQuery,
  onSelect,
}: UsePopoverMultiSelectProps<T>): UsePopoverMultiSelectRes<T> {
  const comboboxItems = useMemo<PopoverComboboxItem[]>(() => {
    return items.map((item) => ({
      key: keyExtractor(item),
      label: labelExtractor(item),
      accessory: accessoryExtractor?.(item),
      content: contentExtractor?.(item),
    }))
  }, [
    items,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    contentExtractor,
  ])

  const [selected, setSelected] = useState<T[]>(defaultSelected ?? [])

  const selectedComboboxItems = useMemo<PopoverComboboxItem[]>(() => {
    return selected.map((item) => ({
      key: keyExtractor(item),
      label: labelExtractor(item),
      accessory: accessoryExtractor?.(item),
      content: contentExtractor?.(item),
    }))
  }, [
    selected,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    contentExtractor,
  ])

  const onSelectComboboxItem = useCallback(
    (ci: PopoverComboboxItem) => {
      const item = items.find((i) => keyExtractor(i) === ci.key)
      if (!item) return
      setSelected((prev) => {
        const alreadySelected = prev.some(
          (prevItem) => keyExtractor(prevItem) === ci.key
        )
        const newSelected = alreadySelected
          ? prev.filter((prevItem) => keyExtractor(prevItem) !== ci.key)
          : [...prev, item]
        onSelect?.(newSelected)
        return newSelected
      })
    },
    [items, keyExtractor, onSelect]
  )

  return {
    popoverMultiSelectProps: {
      items: comboboxItems,
      selected: selectedComboboxItems,
      onSelect: onSelectComboboxItem,
      onChangeQuery,
    },
    selected,
    setSelected,
  }
}

export default usePopoverMultiSelect
